import React, { useState, useEffect } from 'react'
import { Button, Col, message, Radio, Row, Space, Tooltip, Typography } from 'antd'
import { Data, DeliveryCostConfigs } from 'Store/delivery-cost/types'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import { AppInput, LabelItem } from 'Components/form'
import { ActionButton } from 'Components/button'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { cloneDeep } from 'lodash'
import { getDeliveryCostMenu, numberWithComma } from 'helpers/format'
import { DeliveryCostTypeID, menuDeliveryCostType } from 'helpers/constanst'
import DistanceSlider from './DistanceSlider'
import WeightSlider from './WeightSlider'
import { sortingByNumber } from 'helpers/sorting'
const { Paragraph } = Typography

type Props = {
  configData: Data
  type: 'distance' | 'weight'
  onUpdateData: (distanceData: Data) => void
  onDelete?: () => void
}

const CostConfig: React.FunctionComponent<Props> = props => {
  const { type, configData, onUpdateData, onDelete } = props
  const [configList, setConfigList] = useState([] as DeliveryCostConfigs[])
  const [configType, setConfigType] = useState(1 as number)
  const isDistance = type === 'distance'
  const unit = isDistance ? 'กม.' : 'กก.'
  const max = isDistance ? configData.max_distance || 0 : 35000
  const initDataList = {
    start: 0,
    end: max,
    variable_price: 15,
    start_price: 15,
  } as DeliveryCostConfigs

  useEffect(() => {
    if (configList.length !== 0) return
    setConfigList(configData.delivery_cost_configs)
  }, [configData])

  useEffect(() => {
    if (configList.length !== 0) return
    if (configData) {
      setConfigList(sortingByNumber(configData.delivery_cost_configs, 'asc', 'start'))
      const hasStartPrice = configData.delivery_cost_configs[0]?.start_price > 0
      const hasVariablePrice = configData.delivery_cost_configs[0]?.variable_price > 0
      if (hasStartPrice && hasVariablePrice) {
        setConfigType(DeliveryCostTypeID.FIXED_VARIABLE_PRICE)
      } else if (hasStartPrice) {
        setConfigType(DeliveryCostTypeID.FIXED_PRICE)
      } else {
        setConfigType(DeliveryCostTypeID.VARIABLE_PRICE)
      }
    } else {
      setConfigList([cloneDeep(initDataList)])
      setConfigType(1)
    }
  }, [configData])

  const hasVariable =
    configType === DeliveryCostTypeID.VARIABLE_PRICE || configType === DeliveryCostTypeID.FIXED_VARIABLE_PRICE

  const hasFixed =
    configType === DeliveryCostTypeID.FIXED_PRICE || configType === DeliveryCostTypeID.FIXED_VARIABLE_PRICE

  const typeName = isDistance ? 'ระยะทาง' : 'น้ำหนัก'
  const isActive = configList.length > 0

  const getInitialData = (dataType: number) => {
    const variablePrice =
      dataType === DeliveryCostTypeID.VARIABLE_PRICE || dataType === DeliveryCostTypeID.FIXED_VARIABLE_PRICE
        ? 15
        : 0
    const startPrice =
      dataType === DeliveryCostTypeID.FIXED_PRICE || dataType === DeliveryCostTypeID.FIXED_VARIABLE_PRICE
        ? 15
        : 0

    return {
      start: 0,
      end: max,
      variable_price: variablePrice,
      start_price: startPrice,
    } as DeliveryCostConfigs
  }

  const isMaxDistance = () => {
    const result =
      configList && configList.find((item: DeliveryCostConfigs) => item?.end > max || item?.end === max)
    return !!result
  }

  const isLastItem = (index: number) => {
    return configList.length === index + 1
  }

  const lastDistance = (dataLits: DeliveryCostConfigs[]) => {
    const lastIndex = dataLits.length - 1
    return dataLits[lastIndex]?.end || 0
  }

  const onUpdateDistanceData = (index: number, key: string, val: any) => {
    const data = cloneDeep(configList) as any[]
    data[index][key] = val
    setConfigList(data)
    onUpdateData({ ...configData, delivery_cost_configs: data })
  }

  const onChaneDataType = (evt: any) => {
    const type = parseInt(evt.target.value)
    setConfigType(type)
    const data = getInitialData(type)
    const newData = {
      ...data,
    }
    setConfigList([newData])
    onUpdateData({ ...configData, delivery_cost_configs: [newData] })
  }

  const onAddCondition = () => {
    const init = getInitialData(configType)
    const newData = {
      ...init,
      start: lastDistance(configList),
    }
    const data = cloneDeep(configList) as DeliveryCostConfigs[]
    data.push(newData)
    setConfigList(data)
    onUpdateData({ ...configData, delivery_cost_configs: data })
  }

  const onDeleteCondition = (index: number) => {
    const data = cloneDeep(configList) as DeliveryCostConfigs[]
    data.splice(index, 1)
    setConfigList([...data])
    onUpdateData({ ...configData, delivery_cost_configs: [...data] })
  }

  const onEditName = (val: string) => {
    if (val.trim() == '') {
      message.error('กรุณาระบุชื่อ')
    } else {
      onUpdateData({ ...configData, title: val })
    }
  }

  const onEditMaxDistance = (val: string) => {
    const distance = parseInt(val)
    if (!distance || distance < 0 || distance > 200) {
      message.error('กรุณาระบุระยะทางตั้งแต่ 0 - 200 กม.')
    } else {
      onUpdateData({ ...configData, max_distance: parseInt(val) })
    }
  }

  const formatter = (value: any) => {
    return `${numberWithComma(value)} ${unit}`
  }

  const renderSlider = (item: DeliveryCostConfigs, index: number) => {
    const activeSlider = isLastItem(index)
    return isDistance ? (
      <DistanceSlider
        max={configData.max_distance || 0}
        item={item}
        activeSlider={activeSlider}
        onUpdateDistanceData={(key: string, val: any) => onUpdateDistanceData(index, key, val)}
      />
    ) : (
      <WeightSlider
        item={item}
        activeSlider={activeSlider}
        onUpdateDistanceData={(key: string, val: any) => onUpdateDistanceData(index, key, val)}
      />
    )
  }

  const renderInput = (name: string, val: number, index: number) => {
    return (
      <AppInput
        errorMsg="ราคา"
        name={name}
        value={val.toString()}
        isRequired={true}
        isError={!val || val <= 0}
        onChange={val => onUpdateDistanceData(index, name, val)}
        type="number"
        resProps={{ suffix: 'บาท', min: 0, allowClear: false }}
      />
    )
  }

  const renderActionButton = (index: number) => {
    return (
      <ActionButton
        text=""
        icon={<DeleteOutlined />}
        label="ลบ"
        action="deleted"
        onClick={() => onDeleteCondition(index)}
      />
    )
  }

  const deliveryTypeMenu = getDeliveryCostMenu(menuDeliveryCostType, typeName)
  return (
    <Row justify="start" key={type}>
      {isActive && (
        <Col md={24} xs={24}>
          <ConfigWrapper>
            <Row gutter={[16, 16]}>
              {isDistance && (
                <Col md={24} xs={24}>
                  <Row gutter={16} justify="space-between">
                    <Col>
                      {!configData.is_default ? (
                        <Space align="start" direction="vertical" size="small">
                          <Space>
                            ชื่ออัตราค่าขนส่งแบบพิเศษ :
                            <MyParagraph editable={{ onChange: onEditName }} strong>
                              {configData.title}
                            </MyParagraph>
                          </Space>
                          <Space>
                            กำหนดระยะทางสูงสุดที่จัดส่ง <SpanGrey>(สามารถระบุได้ไม่เกิน 200 กม.) </SpanGrey>:
                            <MyParagraph editable={{ onChange: onEditMaxDistance }}>
                              {configData.max_distance?.toString()}
                            </MyParagraph>
                            กม.
                          </Space>
                        </Space>
                      ) : (
                        <MyParagraph editable={false} strong>
                          ระยะการส่งมาตรฐาน 50 กม.
                        </MyParagraph>
                      )}
                    </Col>
                    {!configData.is_default && (
                      <Col>
                        <Button type="primary" shape="circle" danger onClick={onDelete}>
                          ลบ
                        </Button>
                      </Col>
                    )}
                  </Row>
                </Col>
              )}
              <Col md={6} xs={24}>
                <LabelItem label={`วิธีการคำนวน`} /> &nbsp;&nbsp; <br />
                <Radio.Group onChange={onChaneDataType} value={configType}>
                  <Space size="small" direction="vertical">
                    {deliveryTypeMenu.map((item: any, index: number) => {
                      return (
                        <Radio value={item.value} key={index}>
                          <InfoTitle>{item.name}</InfoTitle>
                        </Radio>
                      )
                    })}
                  </Space>
                </Radio.Group>
              </Col>
              <Col md={18} xs={24} style={{ minWidth: '200px', overflowX: 'scroll' }}>
                {/* <LabelItem label={`ตั้งค่าการคำนวน`} /> */}
                <ConfigScroll>
                  <Row justify="start" gutter={[16, 16]} align="middle">
                    <Col span={12}>
                      <InfoTitle>
                        {typeName}เริ่มต้น-สิ้นสุด (สูงสุด {formatter(max)})
                      </InfoTitle>
                    </Col>
                    {hasFixed && (
                      <Col span={5}>
                        <InfoTitle>
                          {configType === DeliveryCostTypeID.FIXED_VARIABLE_PRICE
                            ? 'ราคาเหมาเริ่มต้น'
                            : 'ราคาเหมา'}
                        </InfoTitle>
                      </Col>
                    )}
                    {hasVariable && (
                      <Col span={5}>
                        <InfoTitle>
                          ราคาต่อ{typeName} 1 {unit}
                        </InfoTitle>
                      </Col>
                    )}
                    <Col span={2}></Col>
                  </Row>
                  {configList.map((item: DeliveryCostConfigs, index: number) => {
                    return (
                      <ConfigItem key={index}>
                        <Row justify="start" gutter={[32, 32]} align="middle">
                          <Col span={12}>{renderSlider(item, index)}</Col>
                          {hasFixed && (
                            <Col span={5}>{renderInput('start_price', item.start_price, index)}</Col>
                          )}
                          {hasVariable && (
                            <Col span={5}>{renderInput('variable_price', item.variable_price, index)}</Col>
                          )}
                          <Col span={2}>{isLastItem(index) && index !== 0 && renderActionButton(index)}</Col>
                        </Row>
                      </ConfigItem>
                    )
                  })}
                </ConfigScroll>
                {!isMaxDistance() && (
                  <Row>
                    <Col>
                      <Tooltip
                        placement="top"
                        title={`เพิ่มเงื่อนไขการคำนวนราคาค่าขนส่งสูงสุด ${max} ${unit}`}
                      >
                        <Button type="link" onClick={onAddCondition} icon={<PlusOutlined />}>
                          เพิ่มเงื่อนไข
                        </Button>
                      </Tooltip>
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
          </ConfigWrapper>
        </Col>
      )}
    </Row>
  )
}

const MyParagraph = styled(Paragraph)`
  margin-bottom: 0px !important;
`

const InfoTitle = styled.span`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey};
`

const SpanGrey = styled.span`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey};
`

const ConfigWrapper = styled.div`
  padding: 10px;
  //margin: 10px 0 10px 60px;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  background-color: ${(props: { theme: MyTheme }): string => props.theme.colors.white};
`

const ConfigScroll = styled.div`
  min-width: 650px;
`

const ConfigItem = styled.div`
  margin: 15px 0;
  padding: 10px;
  border-radius: 10px;
  border: 1px dashed #f0f0f0;
  width: 100%;
`

export default CostConfig
